import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  PlayArrow,
  DatasetOutlined,
  SettingsOutlined,
  PreviewOutlined,
  CheckCircleOutline,
  Schedule as ScheduleIcon,
  InfoOutlined,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import apiService from '@signals/app-shell/services/api';
import { useAuthStore } from '@signals/app-shell/stores';
import SimpleProgressTracker from '../components/analysis/SimpleProgressTracker';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';

import {
  AnalysisRequest,
  AnalysisType,
  DeviceType,
  AnalysisParameters,
  AnalysisJob,
  Dataset,
  JobStatus,
  PolAnalysisType,
  ALL_POL_ANALYSIS_TYPES,
  POL_ANALYSIS_TYPE_INFO,
} from '@signals/app-shell/types';

const StudentSelector = React.lazy(() => import('../components/analysis/StudentSelector'));
const DateRangePicker = React.lazy(() => import('../components/analysis/DateRangePicker'));
const DeviceTypeSelector = React.lazy(() => import('../components/analysis/DeviceTypeSelector'));
const AnalysisParametersForm = React.lazy(() => import('../components/analysis/AnalysisParametersForm'));
const PolCriteriaForm = React.lazy(() => import('../components/analysis/PolCriteriaForm'));

interface AnalysisConfig {
  selectedDataset?: Dataset;
  selectedStudents: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  deviceType: DeviceType | 'All';
  deviceTypes: DeviceType[]; // Support multiple device types
  analysisType: AnalysisType;
  parameters: AnalysisParameters;
  /** POL report only: selected Pattern of Life analysis categories. */
  polAnalysisTypes: PolAnalysisType[];
}

const steps = [
  'Configure Data Selection',
  'Set Analysis Parameters',
  'Review & Execute',
  'View Results'
];

const defaultParameters: AnalysisParameters = {
  enable_dwell_detection: true,
  enable_dwell_based_routes: true,
  enable_non_dwell_routes: false,
  enable_temporal_analysis: true,
  enable_connectivity_analysis: false,
  enable_route_consistency: false,
  enable_place_lookup: false,
  enable_ai_insights: false,
  dwell_radius: 50.0,
  stay_point_time_threshold: 300,
  stay_point_distance_threshold: 50.0,
  min_route_length: 5,
  min_segment_distance: 100.0,
  route_similarity_threshold: 50.0,
  max_dwell_locations: 100,
  max_routes: 100,
};

const last24Hours = () => {
  const end = new Date();
  return { start: new Date(end.getTime() - 24 * 60 * 60 * 1000), end };
};

const AnalysisPage: React.FC = () => {
  const { plural, pluralLower } = useParticipantLabels();
  const { user: _user, hasPermission } = useAuthStore();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [currentJob, setCurrentJob] = useState<AnalysisJob | null>(null);
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  
  const [config, setConfig] = useState<AnalysisConfig>({
    selectedStudents: [],
    dateRange: { start: null, end: null },
    deviceType: 'All',
    deviceTypes: [DeviceType.VEHICLE, DeviceType.PHONE, DeviceType.WATCH],
    analysisType: AnalysisType.FULL,
    parameters: defaultParameters,
    polAnalysisTypes: [...ALL_POL_ANALYSIS_TYPES], // POL report defaults to everything
  });

  const isPolReport = config.analysisType === AnalysisType.POL_REPORT;
  
  // Store selected SENTINEL course and courses list
  const [selectedCourse, setSelectedCourse] = useState<string>();
  
  // Memoize handlers to prevent re-renders
  const handleCourseChange = useCallback((courseId: string | undefined) => {
    setSelectedCourse(courseId);
  }, []);
  
  // Start analysis mutation
  const startAnalysisMutation = useMutation({
    mutationFn: (request: AnalysisRequest) => apiService.startAnalysis(request),
    onSuccess: (job) => {
      setCurrentJob(job);
      setActiveStep(3); // Move to results step
    },
    onError: (error: any) => {
      console.error('Analysis failed:', error);
    }
  });

  // Build the parameter preset for a given analysis type. Always returns
  // a fresh object so it can be used as the source of truth on mount,
  // when the user changes type, and when entering the parameters step.
  const getParametersForType = useCallback((type: AnalysisType): AnalysisParameters => {
    const base: AnalysisParameters = {
      enable_dwell_detection: false,
      enable_dwell_based_routes: false,
      enable_non_dwell_routes: false,
      enable_temporal_analysis: false,
      enable_connectivity_analysis: false,
      enable_route_consistency: false,
      enable_place_lookup: false,
      enable_ai_insights: false,
      dwell_radius: 50.0,
      stay_point_time_threshold: 300,
      stay_point_distance_threshold: 50.0,
      min_route_length: 5,
      min_segment_distance: 100.0,
      route_similarity_threshold: 50.0,
      max_dwell_locations: 100,
      max_routes: 100,
    };

    switch (type) {
      case AnalysisType.QUICK:
        return {
          ...base,
          enable_dwell_detection: true,
          enable_temporal_analysis: true,
        };
      case AnalysisType.FULL:
        return {
          ...base,
          enable_dwell_detection: true,
          enable_dwell_based_routes: true,
          enable_non_dwell_routes: true,
          enable_temporal_analysis: true,
          enable_connectivity_analysis: true,
          enable_route_consistency: true,
          enable_place_lookup: true,
          enable_ai_insights: true,
        };
      case AnalysisType.ROUTE_ONLY:
        return {
          ...base,
          enable_dwell_detection: true,
          enable_dwell_based_routes: true,
          enable_non_dwell_routes: true,
          enable_route_consistency: true,
        };
      case AnalysisType.TEMPORAL_ONLY:
        return {
          ...base,
          enable_dwell_detection: true,
          enable_temporal_analysis: true,
        };
      case AnalysisType.MULTI_DEVICE:
        return {
          ...base,
          enable_dwell_detection: true,
          enable_dwell_based_routes: true,
          enable_non_dwell_routes: true,
          enable_temporal_analysis: true,
          enable_connectivity_analysis: true,
          enable_route_consistency: true,
          enable_place_lookup: true,
          enable_ai_insights: true,
        };
      default:
        return base;
    }
  }, []);

  const handleConfigChange = useCallback((updates: Partial<AnalysisConfig>) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      // Whenever the caller passes analysisType, snap parameters to the
      // matching preset. Previously this was guarded by
      // (updates.analysisType !== prev.analysisType), which silently
      // skipped the on-mount sync (since the default state already has
      // FULL selected) and left only the 3 hard-coded defaults toggled.
      if (updates.analysisType) {
        newConfig.parameters = getParametersForType(updates.analysisType);
      }
      if (updates.analysisType === AnalysisType.FULL) {
        newConfig.dateRange = last24Hours();
      }
      return newConfig;
    });
  }, [getParametersForType]);

  // Sync initial parameters with initial analysis type on mount.
  useEffect(() => {
    if (!initialSyncDone) {
      setConfig(prev => ({
        ...prev,
        parameters: getParametersForType(prev.analysisType),
        dateRange: prev.analysisType === AnalysisType.FULL ? last24Hours() : prev.dateRange,
      }));
      setInitialSyncDone(true);
    }
  }, [initialSyncDone, getParametersForType]);

  const handleStartAnalysis = useCallback(() => {
    const polReport = config.analysisType === AnalysisType.POL_REPORT;

    // Check if we have either a dataset or a SENTINEL course selected
    if (!config.selectedDataset && !selectedCourse) {
      alert('Please select a dataset or a SENTINEL course');
      return;
    }

    // POL reports read precomputed course-scoped Pattern of Life data.
    if (polReport && !selectedCourse) {
      alert('Pattern of Life reports require a SENTINEL course');
      return;
    }
    if (polReport && config.polAnalysisTypes.length === 0) {
      alert('Select at least one Pattern of Life analysis type');
      return;
    }
    if (!polReport && config.analysisType === AnalysisType.FULL) {
      const start = config.dateRange.start;
      const end = config.dateRange.end;
      const earliest = new Date(Date.now() - 24 * 60 * 60 * 1000 - 2 * 60 * 1000);
      if (!start || !end || start < earliest || end > new Date(Date.now() + 2 * 60 * 1000) || start > end) {
        alert('Recent Activity Analysis can only cover the last 24 hours');
        return;
      }
    }

    // Determine device type(s) for the request
    const allDeviceTypes = [DeviceType.VEHICLE, DeviceType.PHONE, DeviceType.WATCH];
    const isAllDevices = config.deviceTypes.length === allDeviceTypes.length;

    const allPolSelected =
      config.polAnalysisTypes.length === ALL_POL_ANALYSIS_TYPES.length;

    const request: AnalysisRequest = {
      name: polReport
        ? `Established Pattern of Life - ${new Date().toLocaleDateString()}`
        : `Recent Activity Analysis - ${new Date().toLocaleDateString()}`,
      analysis_type: config.analysisType,
      device_type: isAllDevices ? null : (config.deviceTypes.length === 1 ? config.deviceTypes[0] : null),
      device_types: config.deviceTypes.length > 0 ? config.deviceTypes : undefined,
      dataset_id: config.selectedDataset?.id,
      course_id: selectedCourse, // Include SENTINEL course if selected
      students: config.selectedStudents.length > 0 ? config.selectedStudents : undefined,
      date_range_start: config.dateRange.start?.toISOString(),
      date_range_end: config.dateRange.end?.toISOString(),
      parameters: config.parameters,
      ...(polReport && {
        pol_analysis_types: allPolSelected ? ['all' as const] : config.polAnalysisTypes,
      }),
    };

    startAnalysisMutation.mutate(request);
  }, [config, selectedCourse, startAnalysisMutation]);

  const handleNext = () => {
    const newStep = Math.min(activeStep + 1, steps.length - 1);
    setActiveStep(newStep);
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  // const handleReset = () => {
  //   setActiveStep(0);
  //   setCurrentJob(null);
  //   setConfig({
  //     selectedStudents: [],
  //     dateRange: { start: null, end: null },
  //     deviceType: DeviceType.VEHICLE,
  //     analysisType: AnalysisType.FULL,
  //     parameters: defaultParameters
  //   });
  // };

  // Allow analysis via SENTINEL even when there are no datasets

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: { xs: 1, sm: 2, md: 3 }, px: { xs: 1, sm: 0 } }}>
      {/* Professional Header - Responsive */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', mb: { xs: 2, sm: 4 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 2, sm: 0 }}
          sx={{ pb: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box
              sx={{
                width: 4,
                alignSelf: 'stretch',
                minHeight: 44,
                borderRadius: 1,
                bgcolor: 'secondary.main',
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
                }}
              >
                Pattern of Life Analysis
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  mt: 0.5,
                  fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                }}
              >
                Recent raw activity, or the established nightly Pattern of Life
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => navigate('/scheduled-analyses')}
            size="medium"
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              alignSelf: { xs: 'stretch', sm: 'center' },
              '&:hover': {
                borderColor: 'secondary.main',
                color: 'secondary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            View Schedules
          </Button>
        </Stack>
      </Box>

      {/* Configuration Stepper - Responsive */}
      <Card sx={{ 
        height: 'fit-content', 
        maxWidth: '1200px', 
        mx: 'auto',
        boxShadow: { xs: 1, sm: 3 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 4 } }}>
          <Stepper activeStep={activeStep} orientation="vertical">
                  {/* Step 1: Data Selection */}
                  <Step>
                    <StepLabel 
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: activeStep >= 0 ? 'primary.main' : 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <DatasetOutlined />
                        </Box>
                      )}
                    >
                      <Typography variant="h6">Configure Data Selection</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`Choose ${pluralLower}, date range, and device types`}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {/* Data Selection Row */}
                      <Grid container spacing={3}>
                        {/* Left Column - Course & Students */}
                        <Grid item xs={12} md={5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Data Source
                          </Typography>
                          <React.Suspense fallback={<LinearProgress />}>
                            <StudentSelector
                              selectedDataset={config.selectedDataset}
                              selectedStudents={config.selectedStudents}
                              onDatasetChange={(dataset) => 
                                handleConfigChange({ selectedDataset: dataset })
                              }
                              onStudentsChange={(students) => 
                                handleConfigChange({ selectedStudents: students })
                              }
                              onCourseChange={handleCourseChange}
                            />
                          </React.Suspense>
                        </Grid>

                        {/* Right Column - Date Range */}
                        <Grid item xs={12} md={7}>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Date Range
                            </Typography>
                            <Tooltip
                              title={
                                isPolReport
                                  ? 'Select a date range using quick options or custom dates.'
                                  : 'Recent Activity Analysis can only cover the last 24 hours.'
                              }
                              arrow
                            >
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <React.Suspense fallback={<LinearProgress />}>
                            <DateRangePicker
                              startDate={config.dateRange.start}
                              endDate={config.dateRange.end}
                              maxLookbackHours={isPolReport ? undefined : 24}
                              onChange={(range) => 
                                handleConfigChange({ dateRange: range })
                              }
                            />
                          </React.Suspense>
                        </Grid>

                        {/* Tips Box - Spans Both Columns */}
                        <Grid item xs={12}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(0,0,0,0.2)', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Tips for Analysis Configuration
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary" component="div">
                                  {isPolReport ? (
                                    <>
                                      • Smaller date ranges (7-14 days) = faster reports<br/>
                                      • Larger ranges (30+ days) = a longer established baseline
                                    </>
                                  ) : (
                                    <>
                                      • Recent Activity only covers the last 24 hours<br/>
                                      • Use Established Pattern of Life for older history
                                    </>
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary" component="div">
                                  {`• Select specific ${pluralLower} to focus analysis`}<br/>
                                  {`• Leave ${pluralLower} empty to analyze all available`}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        {/* Device Type & Analysis Configuration */}
                        <Grid item xs={12}>
                          <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
                            <DeviceTypeSelector
                              deviceType={config.deviceType}
                              analysisType={config.analysisType}
                              onChange={(deviceType, analysisType) =>
                                handleConfigChange({ deviceType, analysisType })
                              }
                              deviceTypes={config.deviceTypes}
                              onDeviceTypesChange={(deviceTypes) => {
                                // When deviceTypes change, also update the legacy deviceType field
                                const newDeviceType = deviceTypes.length === 0 
                                  ? DeviceType.PHONE 
                                  : deviceTypes.length === 3 
                                    ? 'All' as const
                                    : deviceTypes[0];
                                handleConfigChange({ deviceTypes, deviceType: newDeviceType });
                              }}
                            />
                          </React.Suspense>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          size="large"
                        >
                          Continue
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 2: Analysis Parameters */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: activeStep >= 1 ? 'primary.main' : 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <SettingsOutlined />
                        </Box>
                      )}
                    >
                      <Typography variant="h6">
                        {isPolReport ? 'Select Report Criteria' : 'Set Analysis Parameters'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isPolReport
                          ? 'Choose which Pattern of Life analysis types to report on'
                          : 'Configure detection thresholds and analysis options'}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
                        {isPolReport ? (
                          <PolCriteriaForm
                            selected={config.polAnalysisTypes}
                            onChange={(polAnalysisTypes) =>
                              handleConfigChange({ polAnalysisTypes })
                            }
                          />
                        ) : (
                          <AnalysisParametersForm
                            parameters={config.parameters}
                            onChange={(parameters) => 
                              handleConfigChange({ parameters })
                            }
                            isAdmin={hasPermission('administrator')}
                          />
                        )}
                      </React.Suspense>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={isPolReport && config.polAnalysisTypes.length === 0}
                          sx={{ mr: 1 }}
                        >
                          Continue
                        </Button>
                        <Button onClick={handleBack}>
                          Back
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 3: Review & Execute */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: activeStep >= 2 ? 'primary.main' : 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PreviewOutlined />
                        </Box>
                      )}
                    >
                      <Typography variant="h6">Review & Execute</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Verify configuration and start analysis
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Grid item xs={6} sm={6} md={4}>
                          <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary">Dataset</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {config.selectedDataset?.name || 'SENTINEL Portal'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary">{plural}</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {config.selectedStudents.length > 0 ? config.selectedStudents.join(', ') : `All ${pluralLower}`}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary">Date Range</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {isPolReport
                                  ? `${config.dateRange.start?.toLocaleDateString()} - ${config.dateRange.end?.toLocaleDateString()}`
                                  : `${config.dateRange.start?.toLocaleString()} - ${config.dateRange.end?.toLocaleString()}`}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card sx={{ bgcolor: 'info.50', height: '100%' }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary">Device Type(s)</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {config.deviceTypes.length === 3 
                                  ? 'All Devices' 
                                  : config.deviceTypes.length === 0 
                                    ? 'None selected'
                                    : config.deviceTypes.join(', ')}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card sx={{ bgcolor: 'info.50', height: '100%' }}>
                            <CardContent>
                              <Typography variant="caption" color="text.secondary">Analysis Type</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {isPolReport ? 'Established Pattern of Life' : 'Recent Activity Analysis'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        {isPolReport ? (
                          <Grid item xs={12} sm={6} md={4}>
                            <Card sx={{ bgcolor: 'success.50', height: '100%' }}>
                              <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                  Report Criteria
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {config.polAnalysisTypes.length === ALL_POL_ANALYSIS_TYPES.length
                                    ? 'All Analysis Types'
                                    : POL_ANALYSIS_TYPE_INFO.filter((t) =>
                                        config.polAnalysisTypes.includes(t.id)
                                      )
                                        .map((t) => t.label)
                                        .join(', ')}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ) : (
                          <Grid item xs={12} sm={6} md={4}>
                            <Card sx={{ bgcolor: config.parameters.enable_ai_insights ? 'success.50' : 'grey.100', height: '100%' }}>
                              <CardContent>
                                <Typography variant="caption" color="text.secondary">AI Insights</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {config.parameters.enable_ai_insights ? 'Enabled' : 'Disabled'}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                      </Grid>
                      {startAnalysisMutation.isPending && (
                        <LinearProgress sx={{ mb: 2 }} />
                      )}
                      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          startIcon={<PlayArrow />}
                          onClick={handleStartAnalysis}
                          disabled={startAnalysisMutation.isPending}
                          color="success"
                          size="large"
                        >
                          {startAnalysisMutation.isPending ? 'Starting...' : 'Run Now'}
                        </Button>
                        {/* Only show Schedule button for analysts and administrators */}
                        {hasPermission('analyst') && (
                          <Button
                            variant="outlined"
                            startIcon={<ScheduleIcon />}
                            onClick={() => {
                              // Store current config in localStorage for schedule dialog
                              localStorage.setItem('pendingScheduleConfig', JSON.stringify(config));
                              navigate('/scheduled-analyses');
                            }}
                            size="large"
                          >
                            Schedule This
                          </Button>
                        )}
                        <Button onClick={handleBack} disabled={startAnalysisMutation.isPending}>
                          Back
                        </Button>
                      </Stack>
                    </StepContent>
                  </Step>

                  {/* Step 4: Results */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: activeStep >= 3 ? 'success.main' : 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CheckCircleOutline />
                        </Box>
                      )}
                    >
                      <Typography variant="h6">View Results</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Analysis complete - view full report
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {currentJob && currentJob.status === JobStatus.COMPLETED && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Analysis completed. Open the full results to review findings.
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              // Navigate to results page
                              window.location.href = `/results/${currentJob.id}`;
                            }}
                          >
                            View Full Results
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
          </Stepper>
        </CardContent>
      </Card>

      {/* Progress Tracker - Full Width */}
      {currentJob && (
        <Card sx={{ 
          mt: 3, 
          maxWidth: '1200px', 
          mx: 'auto',
          boxShadow: 3,
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Analysis in Progress
            </Typography>
            <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
              <SimpleProgressTracker job={currentJob} onJobComplete={(job) => {
                setCurrentJob(job);
                // Move to results step when job completes  
                if (job.status === JobStatus.COMPLETED) {
                  setActiveStep(3);
                }
              }} />
            </React.Suspense>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AnalysisPage;
